import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  XAxis,
  YAxis,
} from 'recharts';
import {
  api,
  type AuthUser,
  type KillGrowth,
  type KillGrowthClass,
  type KillGrowthPlayerRow,
  type KillGrowthRange,
} from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatClassRange, formatGrowthPct, KILL_GROWTH_CLASS_META } from '@/lib/killGrowth';
import { useAllianceLabel } from '@/lib/useCurrentAlliance';
import {
  downloadPoster,
  PosterCard,
  PosterFrame,
  PosterScaledPreview,
} from '@/components/Poster';
import { RosterTabs } from './Roster';

const RANGES: { value: KillGrowthRange; label: string; long: string }[] = [
  { value: '30d', label: '30d', long: 'Last 30 days' },
  { value: '90d', label: '90d', long: 'Last 90 days' },
  { value: '180d', label: '180d', long: 'Last 180 days' },
];

// Rising first — the point of the page is that the small players grow too.
const CLASS_ORDER: KillGrowthClass[] = ['rising', 'mid', 'top'];
const COLOR_ALLIANCE = '#94a3b8';
const POSTER_TOP_N = 10;

function playerLabel(p: { name: string; displayName: string | null }): string {
  return p.displayName?.trim() || p.name;
}

function fmtM(n: number): string {
  return n.toFixed(Math.abs(n) < 10 ? 2 : 1);
}

function shortDate(iso: string): string {
  return `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;
}

export default function AdminKillGrowth({ user }: { user: AuthUser }) {
  const canEdit = user.role === 'R5' || user.role === 'R4';
  const [range, setRange] = useState<KillGrowthRange>('90d');
  const q = useQuery({
    queryKey: ['admin-kill-growth', range],
    queryFn: () => api.getAdminKillGrowth(range),
    enabled: canEdit,
  });
  const posterRef = useRef<HTMLDivElement | null>(null);
  const [busy, setBusy] = useState(false);

  if (!canEdit) {
    return (
      <div className="space-y-4">
        <RosterTabs />
        <h1 className="text-xl font-semibold">Kill growth</h1>
        <p className="rounded border border-slate-800 bg-slate-900/40 p-3 text-sm text-slate-400">
          R4/R5 required.
        </p>
      </div>
    );
  }

  const runExport = async () => {
    if (!posterRef.current) return;
    setBusy(true);
    try {
      const iso = new Date().toISOString().slice(0, 10);
      await downloadPoster(posterRef.current, `kill-growth-${range}-${iso}.png`);
    } catch (err) {
      console.error(err);
      alert('Export failed. Check console.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <RosterTabs />
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold">Kill growth</h1>
          <p className="text-sm text-slate-400">
            Kill development per power class — growth in % so smaller players
            aren't drowned out by the big hitters.
          </p>
        </div>
        <div className="inline-flex shrink-0 rounded-lg border border-slate-800 bg-slate-950 p-0.5 text-sm">
          {RANGES.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => setRange(r.value)}
              className={cn(
                'rounded px-3 py-1 transition',
                range === r.value ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white',
              )}
            >
              {r.label}
            </button>
          ))}
        </div>
      </header>

      {q.isLoading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : q.isError || !q.data ? (
        <div className="text-sm text-rose-300">Could not load kill growth.</div>
      ) : q.data.players.length === 0 ? (
        <div className="rounded border border-slate-800 bg-slate-900/40 p-3 text-sm text-slate-400">
          No player has kill snapshots at both ends of this range.
          {q.data.missingBaseline > 0 && ` (${q.data.missingBaseline} without a baseline.)`}
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
          <PosterCard
            title="Poster"
            description="Class medians, growth trend and most kills gained per class."
            busy={busy}
            onExport={runExport}
          >
            <PosterScaledPreview>
              <div ref={posterRef}>
                <KillGrowthPoster data={q.data} />
              </div>
            </PosterScaledPreview>
          </PosterCard>
          <div className="space-y-4">
            <ClassSummary data={q.data} />
            <PlayerTable data={q.data} />
            <DataNotes data={q.data} />
          </div>
        </div>
      )}
    </div>
  );
}

// -------------------- Interactive part --------------------

function ClassSummary({ data }: { data: KillGrowth }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {CLASS_ORDER.map((key) => {
        const c = data.classes.find((x) => x.key === key)!;
        const meta = KILL_GROWTH_CLASS_META[key];
        return (
          <div key={key} className={cn('rounded-lg border px-3 py-2', meta.chip)}>
            <div className="text-[10px] font-semibold uppercase tracking-wider">
              {meta.label}
            </div>
            {formatClassRange(c.minMSum, c.maxMSum) && (
              <div className="text-[10px] tabular-nums text-slate-400">
                M-Sum {formatClassRange(c.minMSum, c.maxMSum)}
              </div>
            )}
            <div className="text-xl font-semibold tabular-nums text-slate-100">
              {formatGrowthPct(c.medianPct)}
            </div>
            <div className="text-[11px] text-slate-400">
              median{c.medianDeltaM != null && ` +${fmtM(c.medianDeltaM)} M`} ·{' '}
              {c.players} players
            </div>
            {c.noNewKills > 0 && (
              <div className="text-[10px] text-amber-300/90">
                {c.noNewKills} without new kills
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

type SortKey = 'pct' | 'delta';

function PlayerTable({ data }: { data: KillGrowth }) {
  const [filter, setFilter] = useState<KillGrowthClass | 'all'>('all');
  const [sort, setSort] = useState<SortKey>('pct');
  const rows = useMemo(() => {
    const list = data.players.filter((r) => filter === 'all' || r.class === filter);
    return [...list].sort((a, b) => (sort === 'pct' ? b.pct - a.pct : b.deltaM - a.deltaM));
  }, [data.players, filter, sort]);

  return (
    <section className="space-y-2 rounded-lg border border-slate-800 bg-slate-900/40 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1 text-xs">
          {(['all', ...CLASS_ORDER] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setFilter(k)}
              className={cn(
                'rounded border px-2 py-1',
                filter === k
                  ? k === 'all'
                    ? 'border-indigo-500 bg-indigo-600/30 text-indigo-100'
                    : KILL_GROWTH_CLASS_META[k].chip
                  : 'border-slate-700 bg-slate-950 text-slate-400 hover:text-slate-200',
              )}
            >
              {k === 'all' ? 'All' : KILL_GROWTH_CLASS_META[k].label}
            </button>
          ))}
        </div>
        <div className="inline-flex rounded border border-slate-700 bg-slate-950 p-0.5 text-xs">
          {(
            [
              ['pct', 'Sort +%'],
              ['delta', 'Sort +M'],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setSort(k)}
              className={cn(
                'rounded px-2 py-0.5',
                sort === k ? 'bg-slate-700 text-white' : 'text-slate-400',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-left text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="py-1 pr-2">#</th>
              <th className="py-1 pr-2">Player</th>
              <th className="py-1 pr-2">Class</th>
              <th className="py-1 pr-2 text-right">Start</th>
              <th className="py-1 pr-2 text-right">Now</th>
              <th className="py-1 pr-2 text-right">+M</th>
              <th className="py-1 pr-2 text-right">+%</th>
              <th className="py-1">Trend</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <PlayerRow key={r.player.id} row={r} position={i + 1} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function PlayerRow({ row, position }: { row: KillGrowthPlayerRow; position: number }) {
  const meta = KILL_GROWTH_CLASS_META[row.class];
  return (
    <tr className="border-t border-slate-800/70">
      <td className="py-1 pr-2 tabular-nums text-slate-500">{position}</td>
      <td className="max-w-[10rem] truncate py-1 pr-2 text-slate-200" title={playerLabel(row.player)}>
        {playerLabel(row.player)}
      </td>
      <td className="py-1 pr-2">
        <span className={cn('rounded border px-1.5 py-px text-[10px]', meta.chip)}>
          {meta.label}
        </span>
      </td>
      <td
        className="py-1 pr-2 text-right tabular-nums text-slate-400"
        title={
          row.baselineEstimatedFrom
            ? `Estimated for ${row.baselineAt.slice(0, 10)} — interpolated between the reports of ${row.baselineEstimatedFrom[0].slice(0, 10)} and ${row.baselineEstimatedFrom[1].slice(0, 10)}`
            : `Baseline ${row.baselineAt.slice(0, 10)}`
        }
      >
        {row.baselineEstimatedFrom && <span className="text-amber-400/80">≈</span>}
        {fmtM(row.startKills)}
      </td>
      <td
        className="py-1 pr-2 text-right tabular-nums text-slate-300"
        title={`Latest ${row.latestAt.slice(0, 10)}`}
      >
        {fmtM(row.endKills)}
      </td>
      <td className="py-1 pr-2 text-right tabular-nums text-slate-200">+{fmtM(row.deltaM)}</td>
      <td className="py-1 pr-2 text-right font-medium tabular-nums text-slate-100">
        {formatGrowthPct(row.pct)}
      </td>
      <td className="py-1">
        <Sparkline values={row.spark} color={meta.color} />
      </td>
    </tr>
  );
}

function Sparkline({ values, color }: { values: number[]; color: string }) {
  const w = 72;
  const h = 18;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const points = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * w;
      const y = h - 1 - ((v - min) / span) * (h - 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden>
      <polyline points={points} fill="none" stroke={color} strokeWidth={1.5} />
    </svg>
  );
}

function DataNotes({ data }: { data: KillGrowth }) {
  const byPlayer = useMemo(() => {
    const m = new Map<number, { label: string; values: Map<number, number> }>();
    for (const s of data.suspicious) {
      const e = m.get(s.player.id) ?? { label: playerLabel(s.player), values: new Map() };
      e.values.set(s.kills, (e.values.get(s.kills) ?? 0) + 1);
      m.set(s.player.id, e);
    }
    return [...m.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [data.suspicious]);

  const estimated = data.players.filter((r) => r.baselineEstimatedFrom).length;
  if (data.missingBaseline === 0 && byPlayer.length === 0 && estimated === 0) return null;
  return (
    <div className="space-y-1 text-[11px] text-slate-500">
      {estimated > 0 && (
        <p>
          <span className="text-amber-400/80">≈</span> {estimated} start value
          {estimated === 1 ? '' : 's'} estimated — the player didn't report kills around
          the range start, so the value is interpolated between the reports before and
          after it (hover the start value for the dates).
        </p>
      )}
      {data.missingBaseline > 0 && (
        <p>
          {data.missingBaseline} active player{data.missingBaseline === 1 ? '' : 's'} left out —
          no kill snapshot near the start of the range, or no newer one since.
        </p>
      )}
      {byPlayer.length > 0 && (
        <details>
          <summary className="cursor-pointer hover:text-slate-300">
            {data.suspicious.length} snapshot{data.suspicious.length === 1 ? '' : 's'} of{' '}
            {byPlayer.length} player{byPlayer.length === 1 ? '' : 's'} ignored as implausible
            (higher than a later value) — check in the player drawer
          </summary>
          <ul className="mt-1 space-y-0.5 pl-3">
            {byPlayer.map((p) => (
              <li key={p.label}>
                <span className="text-slate-300">{p.label}</span>:{' '}
                {[...p.values.entries()]
                  .map(([v, n]) => (n > 1 ? `${v} M (${n}×)` : `${v} M`))
                  .join(', ')}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

// -------------------- Poster --------------------

function KillGrowthPoster({ data }: { data: KillGrowth }) {
  const allianceLabel = useAllianceLabel();
  const rangeLong = RANGES.find((r) => r.value === data.range)?.long ?? data.range;
  // The API sends medians indexed to 100 at the start; the poster shows plain
  // growth (+12 % instead of 112) so it reads without explanation.
  const growth = (v: number | null | undefined) => (v == null ? null : v - 100);
  const chartData = data.samples.map((iso, i) => ({
    label: shortDate(iso),
    rising: growth(data.classes.find((c) => c.key === 'rising')?.indexed[i]),
    mid: growth(data.classes.find((c) => c.key === 'mid')?.indexed[i]),
    top: growth(data.classes.find((c) => c.key === 'top')?.indexed[i]),
    alliance: growth(data.allianceIndexed[i]),
  }));
  const values = chartData.flatMap((d) =>
    [d.rising, d.mid, d.top, d.alliance].filter((v): v is number => v != null),
  );
  const yMax = Math.ceil(Math.max(1, ...values) / 5) * 5;
  const topByClass = Object.fromEntries(
    CLASS_ORDER.map((key) => [
      key,
      // Poster ranks by absolute kills gained; the table can still sort by %.
      data.players
        .filter((r) => r.class === key)
        .sort((a, b) => b.deltaM - a.deltaM)
        .slice(0, POSTER_TOP_N),
    ]),
  ) as Record<KillGrowthClass, KillGrowthPlayerRow[]>;

  return (
    <PosterFrame
      accent="#f43f5e"
      eyebrow="Alliance kill growth"
      title={`${allianceLabel} · Kill growth`}
      weekLabel={`${rangeLong} · ${shortDate(data.startIso)} – ${shortDate(data.endIso)}`}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18, height: '100%' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
          {CLASS_ORDER.map((key) => {
            const c = data.classes.find((x) => x.key === key)!;
            const meta = KILL_GROWTH_CLASS_META[key];
            return (
              <div
                key={key}
                style={{
                  borderRadius: 16,
                  border: `2px solid ${meta.color}66`,
                  background: `${meta.color}14`,
                  padding: '14px 18px',
                }}
              >
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    letterSpacing: 2,
                    textTransform: 'uppercase',
                    color: meta.color,
                  }}
                >
                  {meta.label}
                </div>
                <div style={{ fontSize: 13, color: '#94a3b8' }}>{meta.sub}</div>
                {formatClassRange(c.minMSum, c.maxMSum) && (
                  <div
                    style={{
                      fontSize: 13,
                      color: '#cbd5e1',
                      fontVariantNumeric: 'tabular-nums',
                      marginTop: 2,
                    }}
                  >
                    M-Sum {formatClassRange(c.minMSum, c.maxMSum)}
                  </div>
                )}
                <div
                  style={{
                    fontSize: 44,
                    fontWeight: 800,
                    marginTop: 6,
                    fontVariantNumeric: 'tabular-nums',
                    color: '#f8fafc',
                  }}
                >
                  {formatGrowthPct(c.medianPct)}
                </div>
                <div style={{ fontSize: 15, color: '#cbd5e1' }}>
                  median{c.medianDeltaM != null && ` +${fmtM(c.medianDeltaM)} M kills`}
                </div>
                <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
                  {c.players} players · total +{fmtM(c.totalDeltaM)} M
                </div>
                {c.noNewKills > 0 && (
                  <div style={{ fontSize: 13, color: '#fbbf24cc', marginTop: 2 }}>
                    {c.noNewKills} player{c.noNewKills === 1 ? '' : 's'} without new kills
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div
          style={{
            borderRadius: 16,
            border: '1px solid #1e293b',
            background: '#0f172a99',
            padding: '14px 16px 6px 4px',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              padding: '0 0 6px 18px',
            }}
          >
            <span style={{ fontSize: 16, fontWeight: 700, color: '#e2e8f0' }}>
              Kill growth since start (typical player per class)
            </span>
            <span style={{ display: 'flex', gap: 16, fontSize: 13, color: '#94a3b8' }}>
              {CLASS_ORDER.map((key) => (
                <LegendSwatch key={key} color={KILL_GROWTH_CLASS_META[key].color} label={KILL_GROWTH_CLASS_META[key].label} />
              ))}
              <LegendSwatch color={COLOR_ALLIANCE} label="Alliance" dashed />
            </span>
          </div>
          <LineChart width={1000} height={300} data={chartData} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#1e293b" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fill: '#64748b', fontSize: 13 }}
              stroke="#334155"
              interval="preserveStartEnd"
              minTickGap={30}
            />
            <YAxis
              tick={{ fill: '#64748b', fontSize: 13 }}
              stroke="#334155"
              width={60}
              domain={[0, yMax]}
              allowDecimals={false}
              tickFormatter={(v: number) => (v === 0 ? '0 %' : `+${v} %`)}
            />
            <ReferenceLine y={0} stroke="#475569" />
            <Line
              dataKey="alliance"
              stroke={COLOR_ALLIANCE}
              strokeWidth={2}
              strokeDasharray="6 5"
              dot={false}
              isAnimationActive={false}
              connectNulls
            />
            {CLASS_ORDER.map((key) => (
              <Line
                key={key}
                dataKey={key}
                stroke={KILL_GROWTH_CLASS_META[key].color}
                strokeWidth={key === 'rising' ? 4 : 3}
                dot={false}
                isAnimationActive={false}
                connectNulls
              />
            ))}
          </LineChart>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, flex: 1, minHeight: 0 }}>
          {CLASS_ORDER.map((key) => (
            <TopGrowersColumn key={key} cls={key} rows={topByClass[key]} />
          ))}
        </div>
        {CLASS_ORDER.some((key) => topByClass[key].some((r) => r.baselineEstimatedFrom)) && (
          <div style={{ fontSize: 12, color: '#64748b', marginTop: -8 }}>
            ≈ start value interpolated between two kill reports
          </div>
        )}
      </div>
    </PosterFrame>
  );
}

function LegendSwatch({ color, label, dashed }: { color: string; label: string; dashed?: boolean }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span
        style={{
          width: 18,
          height: 0,
          borderTop: `3px ${dashed ? 'dashed' : 'solid'} ${color}`,
          display: 'inline-block',
        }}
      />
      {label}
    </span>
  );
}

function TopGrowersColumn({ cls, rows }: { cls: KillGrowthClass; rows: KillGrowthPlayerRow[] }) {
  const meta = KILL_GROWTH_CLASS_META[cls];
  return (
    <div
      style={{
        borderRadius: 16,
        border: '1px solid #1e293b',
        background: '#0f172a99',
        padding: '12px 14px',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          fontSize: 14,
          fontWeight: 700,
          letterSpacing: 2,
          textTransform: 'uppercase',
          color: meta.color,
          marginBottom: 8,
        }}
      >
        Most kills gained · {meta.label}
      </div>
      {rows.map((r, i) => (
        <div
          key={r.player.id}
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 8,
            fontSize: 16,
            lineHeight: '30px',
            borderTop: i === 0 ? undefined : '1px solid #1e293b',
          }}
        >
          <span style={{ width: 22, textAlign: 'right', color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>
            {i + 1}.
          </span>
          <span
            style={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: i < 3 ? '#f8fafc' : '#cbd5e1',
              fontWeight: i < 3 ? 700 : 500,
            }}
          >
            {playerLabel(r.player)}
          </span>
          <span style={{ fontWeight: 700, color: meta.color, fontVariantNumeric: 'tabular-nums' }}>
            {r.baselineEstimatedFrom ? '≈' : ''}+{fmtM(r.deltaM)} M
          </span>
          <span style={{ width: 70, textAlign: 'right', fontSize: 13, color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>
            {formatGrowthPct(r.pct)}
          </span>
        </div>
      ))}
    </div>
  );
}
